(() => {
  // Prevent duplicate script execution when section is duplicated on the same page.
  if (window.__metiProductUgcVideosScriptLoaded) {
    if (typeof window.__metiInitProductUgcVideos === 'function') {
      window.__metiInitProductUgcVideos();
    }
    return;
  }
  window.__metiProductUgcVideosScriptLoaded = true;

class ProductUgcVideos {
  constructor(section) {
    this.section = section;
    this.slider = section.querySelector('.product-ugc-videos__slider');
    this.viewport = section.querySelector('.product-ugc-videos__viewport');
    this.track = section.querySelector('.product-ugc-videos__track');
    this.slides = Array.from(section.querySelectorAll('.product-ugc-videos__slide'));
    this.prevButton = section.querySelector('.product-ugc-videos__control--prev');
    this.nextButton = section.querySelector('.product-ugc-videos__control--next');
    this.dotsContainer = section.querySelector('.product-ugc-videos__dots');
    this.cards = Array.from(section.querySelectorAll('.product-ugc-videos__card'));
    this.desktopVisible = Number(this.slider?.dataset.desktopVisible) || 3;
    this.mobileVisible = Number(this.slider?.dataset.mobileVisible) || 1;

    this.pageStarts = [];
    this.currentPage = 0;
    this.ticking = false;

    if (
      !this.slider ||
      !this.viewport ||
      !this.track ||
      this.slides.length === 0 ||
      !this.prevButton ||
      !this.nextButton ||
      !this.dotsContainer
    ) {
      return;
    }

    this.handleResize = this.handleResize.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
    this.handlePrev = this.handlePrev.bind(this);
    this.handleNext = this.handleNext.bind(this);

    this.prevButton.addEventListener('click', this.handlePrev);
    this.nextButton.addEventListener('click', this.handleNext);
    this.viewport.addEventListener('scroll', this.handleScroll, { passive: true });
    window.addEventListener('resize', this.handleResize);

    this.setupVideos();
    this.setup();
  }

  isDesktop() {
    return window.matchMedia('(min-width: 990px)').matches;
  }

  getVisibleSlides() {
    return this.isDesktop() ? this.desktopVisible : this.mobileVisible;
  }

  getPageStarts() {
    const visibleSlides = this.getVisibleSlides();
    const totalSlides = this.slides.length;

    if (totalSlides <= visibleSlides) return [0];

    const starts = [];
    for (let index = 0; index <= totalSlides - visibleSlides; index += 1) {
      starts.push(index);
    }

    return starts;
  }

  getLeadingOffset() {
    return this.slides[0]?.offsetLeft || 0;
  }

  getScrollPosition(startIndex) {
    const targetSlide = this.slides[startIndex];
    if (!targetSlide) return 0;

    const scrollPosition = targetSlide.offsetLeft - this.getLeadingOffset();
    return Math.max(0, scrollPosition);
  }

  buildDots() {
    this.dotsContainer.innerHTML = '';

    this.pageStarts.forEach((startIndex, pageIndex) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'product-ugc-videos__dot';
      dot.setAttribute('aria-label', `Go to video page ${pageIndex + 1}`);
      dot.addEventListener('click', () => {
        this.goToPage(pageIndex);
      });
      this.dotsContainer.appendChild(dot);
    });
  }

  updateDots() {
    const dots = this.dotsContainer.querySelectorAll('.product-ugc-videos__dot');
    dots.forEach((dot, dotIndex) => {
      const isActive = dotIndex === this.currentPage;
      dot.classList.toggle('is-active', isActive);
      if (isActive) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }

  updateControls() {
    const isSinglePage = this.pageStarts.length <= 1;
    this.slider.classList.toggle('is-single-page', isSinglePage);

    this.prevButton.disabled = isSinglePage || this.currentPage <= 0;
    this.nextButton.disabled = isSinglePage || this.currentPage >= this.pageStarts.length - 1;
    this.updateDots();
  }

  goToPage(pageIndex, behavior = 'smooth') {
    const safeIndex = Math.max(0, Math.min(pageIndex, this.pageStarts.length - 1));
    this.currentPage = safeIndex;

    const startIndex = this.pageStarts[safeIndex];
    const left = this.getScrollPosition(startIndex);

    this.viewport.scrollTo({
      left,
      behavior,
    });

    this.updateControls();
  }

  syncPageFromScroll() {
    const currentScroll = this.viewport.scrollLeft;

    let nearestPage = 0;
    let nearestDistance = Number.POSITIVE_INFINITY;

    this.pageStarts.forEach((startIndex, pageIndex) => {
      const targetScroll = this.getScrollPosition(startIndex);
      const distance = Math.abs(currentScroll - targetScroll);

      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestPage = pageIndex;
      }
    });

    this.currentPage = nearestPage;
    this.updateControls();
  }

  setup() {
    this.pageStarts = this.getPageStarts();
    this.currentPage = Math.min(this.currentPage, this.pageStarts.length - 1);

    this.buildDots();
    this.goToPage(this.currentPage, 'auto');
    this.syncPageFromScroll();
  }

  handleScroll() {
    if (this.ticking) return;

    this.ticking = true;
    window.requestAnimationFrame(() => {
      this.syncPageFromScroll();
      this.ticking = false;
    });
  }

  handleResize() {
    this.setup();
  }

  handlePrev() {
    this.goToPage(this.currentPage - 1);
  }

  handleNext() {
    this.goToPage(this.currentPage + 1);
  }

  pauseOtherVideos(activeVideo) {
    this.cards.forEach((card) => {
      const video = card.querySelector('video');
      if (!video || video === activeVideo) return;
      video.pause();
      card.classList.remove('is-playing');
    });
  }

  toggleVideo(card, video) {
    if (video.paused) {
      video.muted = false;
      video.removeAttribute('muted');
      this.pauseOtherVideos(video);
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    } else {
      video.pause();
    }
  }

  setupVideos() {
    this.cards.forEach((card) => {
      const video = card.querySelector('video');
      const playToggle = card.querySelector('[data-play-toggle]');

      if (!video || !playToggle) return;

      video.muted = false;
      video.removeAttribute('muted');
      video.playsInline = true;

      playToggle.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.toggleVideo(card, video);
      });

      video.addEventListener('click', () => {
        this.toggleVideo(card, video);
      });

      video.addEventListener('play', () => {
        this.pauseOtherVideos(video);
        card.classList.add('is-playing');
      });

      video.addEventListener('pause', () => {
        card.classList.remove('is-playing');
      });
    });
  }
}

function initProductUgcVideos() {
  const sections = document.querySelectorAll('.product-ugc-videos');

  sections.forEach((section) => {
    if (section.dataset.ugcVideosInitialized === 'true') return;
    section.dataset.ugcVideosInitialized = 'true';
    new ProductUgcVideos(section);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProductUgcVideos);
} else {
  initProductUgcVideos();
}

document.addEventListener('shopify:section:load', initProductUgcVideos);
window.__metiInitProductUgcVideos = initProductUgcVideos;
})();
